// A tab opened before a deploy still asks for the old hashed chunks, which the
// new image no longer serves: every lazy route or component then fails to load
// (#1719). The fix is a reload, which fetches the new index.html and chunks.
// It happens once: a second failure within RELOAD_GUARD_MS shows the crash
// page (with its « Reload » button) instead of looping.

export const STALE_CHUNK_RELOAD_KEY = 'stale-chunk-reload-at'
export const RELOAD_GUARD_MS = 10_000

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>

const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's CSS preload
]

// isStaleChunkError tells whether error is a failed load of a code-split chunk.
export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false
  const name = (error as { name?: unknown }).name
  if (name === 'ChunkLoadError') return true
  const message = error instanceof Error ? error.message : String(error)
  return STALE_CHUNK_MESSAGES.some(m => message.includes(m))
}

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

// canAutoReload is false when an automatic reload already happened in the
// last RELOAD_GUARD_MS, so a chunk that really is missing doesn't loop.
export function canAutoReload(storage: Storage | null = sessionStore(), now = Date.now()): boolean {
  if (!storage) return false
  const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY) ?? 0)
  return !(last > 0 && now - last < RELOAD_GUARD_MS)
}

// reloadForStaleChunk reloads the page on the same URL, unless the guard
// forbids it. It returns whether it reloaded.
export function reloadForStaleChunk(
  storage: Storage | null = sessionStore(),
  now = Date.now(),
  reload: () => void = () => window.location.reload(),
): boolean {
  if (!storage || !canAutoReload(storage, now)) return false
  storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now))
  reload()
  return true
}

// installStaleChunkReload listens to Vite's preload failures: the reload
// replaces the error, so the router never renders the crash page for it.
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', event => {
    if (reloadForStaleChunk()) event.preventDefault()
  })
}
